import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../api";
import SEO from "../components/SEO";
import EditorialHero from "../components/EditorialHero";
import HorizontalScroll from "../components/HorizontalScroll";

/* ── Category definitions ── */
const STORE_CATEGORIES = [
  { id: "고속인쇄기", label: "고속 인쇄기", img: "/api/category-images/1.고속인쇄기.webp" },
  { id: "디자인용복합기", label: "디자인용 복합기", img: "/api/category-images/2.복합기.webp" },
  { id: "사무용복합기", label: "사무용 복합기", img: "/api/category-images/2.복합기.webp" },
  { id: "소형복합기", label: "소형복합기", img: "/api/category-images/3.소형복합기.webp" },
  { id: "프린터", label: "프린터", img: "/api/category-images/4.프린터.webp" },
  { id: "토너소모품", label: "토너·소모품", img: "/api/category-images/5.토너.webp" },
];

/* ── Product Card ── */
function ProductCard({ product }) {
  const isToner = (product.store_category || "").includes("토너");
  const price = product.price;
  return (
    <Link to={product.product_url || `/p/${product.slug}`}
      className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-gray-200 transition-all duration-300">
      <div className="aspect-square bg-gray-50 flex items-center justify-center p-4 overflow-hidden">
        {product.image_url ? (
          <img src={product.image_url} alt={product.name}
            className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        ) : (
          <div className="text-[12px] font-bold tracking-wide text-gray-400">{isToner ? "TONER" : "OFFICE"}</div>
        )}
      </div>
      <div className="p-4">
        <p className="text-[11px] text-gray-400 mb-1">{product.brand || product.store_category}</p>
        <h3 className="text-[14px] font-medium text-gray-900 line-clamp-2 leading-snug mb-2">{product.name}</h3>
        {price > 0 && (
          <p className="text-[16px] font-bold text-gray-900">{price.toLocaleString()}원</p>
        )}
      </div>
    </Link>
  );
}

export default function B2BStorePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [b2bOk, setB2bOk] = useState(null); // null = checking
  const [searchQ, setSearchQ] = useState("");
  const [selectedCat, setSelectedCat] = useState("all");
  const searchRef = useRef(null);

  // B2B 권한 확인
  useEffect(() => {
    if (!user) { navigate("/b2b", { replace: true }); return; }
    api.get("/api/b2b/status")
      .then(({ data }) => {
        if (data.status === "approved") {
          setB2bOk(true);
        } else {
          navigate("/b2b", { replace: true });
        }
      })
      .catch(() => navigate("/b2b", { replace: true }));
  }, [user, navigate]);

  // 상품 로드
  useEffect(() => {
    if (!b2bOk) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (searchQ) params.set("q", searchQ);
    if (selectedCat && selectedCat !== "all") params.set("store_category", selectedCat);
    api.get(`/api/b2b/products?${params}`)
      .then(({ data }) => setProducts(data.products || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [b2bOk, searchQ, selectedCat]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQ(searchRef.current?.value || "");
  };

  const grouped = useMemo(() => {
    if (selectedCat && selectedCat !== "all") return null;
    const map = {};
    for (const cat of STORE_CATEGORIES) map[cat.id] = [];
    for (const p of products) {
      const sc = p.store_category || "사무용복합기";
      if (map[sc]) map[sc].push(p);
    }
    return map;
  }, [products, selectedCat]);

  if (b2bOk === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO title="사업자 전용몰 - 일광오피스" description="B2B 사업자 전용 구매몰" path="/b2b/store" />

      {/* Hero */}
      <EditorialHero
        eyebrow="B2B · 사업자 전용"
        titleTop="사업자 전용"
        titleItalic="business hub."
        description="사업자를 위한 특별한 가격, 정식 공급의 안정성. 등록된 사업자 회원만 만날 수 있는 전용몰."
      >
        <form onSubmit={handleSearch} style={{ position: "relative", maxWidth: 520 }}>
          <input
            ref={searchRef}
            type="text"
            defaultValue={searchQ}
            placeholder="제품명, 브랜드로 검색"
            style={{
              width: "100%",
              padding: "14px 44px 14px 20px",
              fontSize: 15,
              color: "white",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: 999,
              outline: "none",
              backdropFilter: "blur(12px)",
            }}
          />
          <button
            type="submit"
            style={{
              position: "absolute",
              right: 14,
              top: "50%",
              transform: "translateY(-50%)",
              color: "rgba(255,255,255,0.7)",
              background: "transparent",
            }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        </form>
      </EditorialHero>

      {/* Categories */}
      <section className="bg-[#f5f5f7] border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <HorizontalScroll label="사업자몰 상품 분류">
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
            <button onClick={() => { setSelectedCat("all"); setSearchQ(""); }}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition ${selectedCat === "all" ? "bg-gray-900 text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>
              전체
            </button>
            {STORE_CATEGORIES.map(c => (
              <button key={c.id} onClick={() => { setSelectedCat(c.id); setSearchQ(""); }}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition ${selectedCat === c.id ? "bg-gray-900 text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>
                {c.label}
              </button>
            ))}
          </div>
          </HorizontalScroll>
        </div>
      </section>

      {/* Products */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <p className="text-lg">등록된 상품이 없습니다</p>
          </div>
        ) : selectedCat !== "all" || searchQ ? (
          /* Grid — filtered or searched */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            {products.map(p => <ProductCard key={`${p._source}-${p.id}`} product={p} />)}
          </div>
        ) : (
          /* Grouped by category */
          STORE_CATEGORIES.map(cat => {
            const items = grouped?.[cat.id] || [];
            if (!items.length) return null;
            return (
              <div key={cat.id} className="mb-12">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xl font-bold text-gray-900">{cat.label}</h2>
                  <button onClick={() => setSelectedCat(cat.id)} className="text-sm text-blue-500 hover:text-blue-700">
                    전체 보기 →
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
                  {items.slice(0, 4).map(p => <ProductCard key={`${p._source}-${p.id}`} product={p} />)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
